import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckoutSteps } from "@/components/billing/CheckoutSteps";

export default function CheckoutResultPage({ status }: { status: "success" | "cancel" }) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const packageId = params.get("package");
  const mode = params.get("mode");
  const sessionId = params.get("session_id");
  const success = status === "success";
  const manual = success && mode === "manual";
  const subscriptionUrl = sessionId
    ? `/billing/subscription?checkout=success&session_id=${encodeURIComponent(sessionId)}`
    : "/billing/subscription";

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 px-6 py-4 backdrop-blur-xl">
        <h1 className="text-lg font-semibold">{manual ? t("checkout.result.manualTitle") : success ? t("checkout.result.successTitle") : t("checkout.result.cancelTitle")}</h1>
        <p className="text-xs text-muted-foreground">{t("checkout.result.subtitle")}</p>
      </header>
      <main className="mx-auto max-w-3xl space-y-6 p-6">
        <CheckoutSteps current={success ? 5 : 4} />
        <Card>
          <CardContent className="space-y-5 p-8 text-center">
            {success ? <CheckCircle2 className="mx-auto h-12 w-12 text-success" /> : <XCircle className="mx-auto h-12 w-12 text-destructive" />}
            <div>
              <h2 className="text-2xl font-bold">{manual ? t("checkout.result.manualHeading") : success ? t("checkout.result.successHeading") : t("checkout.result.cancelHeading")}</h2>
              <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-muted-foreground">
                {manual
                  ? t("checkout.result.manualDescription")
                  : success
                  ? t("checkout.result.successDescription")
                  : t("checkout.result.cancelDescription")}
              </p>
            </div>
            <div className="flex flex-col justify-center gap-2 sm:flex-row">
              {success ? (
                <Button asChild><Link to={subscriptionUrl}>{t("checkout.actions.viewSubscription")}</Link></Button>
              ) : (
                <Button asChild><Link to={packageId ? `/checkout/package/${packageId}` : "/billing/plans"}>{t("checkout.actions.resume")}</Link></Button>
              )}
              <Button asChild variant="outline"><Link to="/billing/invoices">{t("checkout.actions.viewInvoices")}</Link></Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
